import {
  ApolloCache,
  ApolloClient,
  ApolloLink,
  from,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { onError } from '@apollo/client/link/error';
import { HttpLink } from '@apollo/client/link/http';
import * as Sentry from '@sentry/nextjs';

import { clientConfig } from '@/config/clientConfig';
import { getBearerToken } from '@/shared/api/utils';
import { BlockLink } from '@/shared/services/apollo/block-link';

type ApolloClientType = ApolloClient<NormalizedCacheObject>;
interface ApolloClientParameters {
  cache?: ApolloCache<NormalizedCacheObject>;
  authorizationHeader?: string;
}

// https://www.apollographql.com/docs/react/api/link/introduction/
// https://blog.sentry.io/handling-graphql-errors-using-sentry/
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, extensions }) => {
      Sentry.withScope((scope) => {
        scope.setTag('code', String(extensions.code));
        scope.setExtra('code', String(extensions.code));
        scope.setExtra('message', message);
        Sentry.captureException(new Error('graphQLErrors'));
      });
    });
  }

  if (networkError) {
    Sentry.withScope(() => {
      Sentry.captureException(new Error('Network error'));
    });
  }
});

function getLink(authHeader: string | undefined): ApolloLink | undefined {
  if (clientConfig.isExported) {
    return new BlockLink();
  } else {
    const isMergedEnabled = clientConfig.api.isGraphqlMergedRequestEnabled;
    const isBrowser = typeof window !== 'undefined';

    if (isMergedEnabled && isBrowser) {
      return new BatchHttpLink({
        uri: clientConfig.api.graphqlUrl,
        batchMax: Number.parseInt(
          // process.env.NEXT_PUBLIC_MERGED_GRAPHQL_REQUESTS_BATCH_MAX || '20',
          clientConfig.api.graphqlMergedRequestBatchMax || '20',
        ), // No more than 7 operations per batch
        batchInterval: Number.parseInt(
          // process.env.NEXT_PUBLIC_MERGED_GRAPHQL_REQUESTS_BATCH_INTERVAL || '20',
          clientConfig.api.graphqlMergedRequestBranchInterval || '20',
        ), // Wait no more than 20ms after first batched operation
      });
    }
  }
  return from([
    errorLink,
    new HttpLink({
      uri: clientConfig.api.graphqlUrl,
      ...(authHeader && {
        headers: {
          authorization: getBearerToken(authHeader) as string,
        },
      }),
    }),
  ]);
}

export function getApolloClient({
  cache,
  authorizationHeader,
}: ApolloClientParameters): ApolloClientType {
  return new ApolloClient({
    link: getLink(authorizationHeader),
    ...(authorizationHeader && {
      headers: {
        authorization: getBearerToken(authorizationHeader) as string,
      },
    }),
    uri:
      clientConfig.api.isGraphqlMergedRequestEnabled && typeof window !== 'undefined'
        ? undefined
        : clientConfig.api.graphqlUrl,
    cache: cache || new InMemoryCache(),
    connectToDevTools: clientConfig.isDebug,
    defaultOptions: {
      query: {
        fetchPolicy: 'cache-first',
      },
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
      mutate: {
        fetchPolicy: 'network-only',
      },
    },
  });
}
